import { useLearningProgressLearnerDetail } from "@/api/services/main/learning-progress";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { LearnerInfoSection } from "@/features/learning-progress/components/learner-detail/LearnerInfoSection";
import { CurrentProgressSection } from "@/features/learning-progress/components/learner-detail/CurrentProgressSection";
import { CommentsSection } from "@/features/learning-progress/components/learner-detail/CommentsSection";
import { useLearnerNotesPermission } from "@/features/learning-progress/hooks/useLearnerNotesPermission";
import { AppRoutes } from "@/routes/config";
import { black, button, divider, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { toNumber } from "@/utils/number";
import { sleep } from "@/utils/pieces";
import { getDynamicRoute } from "@/utils/url";
import { Box, Grid, Stack, Typography, css, styled } from "@mui/material";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";

const PAGE_WIDTH = 1150;
const CONTENT_WIDTH = 900;
const LEFT_COL_WIDTH = 508;
const RIGHT_COL_WIDTH = 360;
const STICKY_TOP = 80;

export const LearningProgressLearnerDetailPage: FC = () => {
  const navigate = useNavigate();
  const { industryId, learnerId } = useParams();
  const { data: learnerDetailRes } = useLearningProgressLearnerDetail({ learnerId: learnerId ?? "" });
  const learnerInfo = learnerDetailRes?.data.learnerInfo;
  const notes = learnerDetailRes?.data.notes;
  const { companyReport, memo } = useLearnerNotesPermission();

  const menuTitles = useMemo(() => [
    "外国人情報",
    "現在の進行情報",
    ...(companyReport.visible ? ["所属機関様への報告"] : []),
    ...(memo.visible ? [memo.title] : []),
  ], [companyReport.visible, memo.visible, memo.title]);

  const handleEdit = () => {
    navigate(
      getDynamicRoute(AppRoutes.learningProgressLearnerEdit, {
        industryId,
        learnerId,
      }),
    );
  };

  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const disableScrollSpy = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [leftColEl, setLeftColEl] = useState<HTMLDivElement | null>(null);
  const [leftColHeight, setLeftColHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!leftColEl || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      setLeftColHeight(entries[0]?.contentRect.height);
    });
    observer.observe(leftColEl);
    return () => observer.disconnect();
  }, [leftColEl]);

  const setSectionRef = (index: number) => (el: HTMLDivElement | null) => {
    sectionRefs.current[index] = el;
  };

  const handleClickMenuItem = async (index: number) => {
    const el = sectionRefs.current[index];
    if (!el) return;
    disableScrollSpy.current = true;
    const position = el.getBoundingClientRect();
    window.scrollTo({
      behavior: "smooth",
      left: position.left,
      top: position.top + window.scrollY - STICKY_TOP,
    });
    setActiveIndex(index);
    await sleep(800);
    disableScrollSpy.current = false;
  };

  useEffect(() => {
    const handleScroll = () => {
      if (disableScrollSpy.current) return;
      const isHalfBottom =
        window.scrollY > toNumber(containerRef.current?.clientHeight) / 3;
      sectionRefs.current.forEach((el, index) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top - window.innerHeight + (window.innerHeight * (isHalfBottom ? 2 : 5)) / 7 < 0) {
          setActiveIndex(index);
        }
      });
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Page Title */}
      <TitleContainer>
        <Grid container direction="column" gap={1}>
          <Typography variant="head20Bold">
            学習進捗{learnerInfo ? ` - ${learnerInfo.fullName}` : ""}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
              登録日：<span>{learnerInfo?.registeredAt ?? ""}</span>
            </Typography>
            <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
              更新日：<span>{learnerInfo?.updatedAt ?? ""}</span>
            </Typography>
          </Box>
        </Grid>
      </TitleContainer>

      <PageWrap ref={containerRef}>
        {/* Sticky left menu */}
        <Menu>
          <Typography variant="body14Bold" sx={{ mb: 1 }}>
            {learnerInfo?.fullName ?? ""}
          </Typography>
          {menuTitles.map((title, index) => (
            <MenuItem key={title} active={activeIndex === index} onClick={() => handleClickMenuItem(index)}>
              {title}
            </MenuItem>
          ))}
        </Menu>

        {/* Content area */}
        <ContentArea>
          {/* Sections 1 + 2 in left column; Comments in right column spanning both */}
          <Stack direction="row" gap={4} alignItems="flex-start">
            <Stack ref={setLeftColEl} sx={{ width: LEFT_COL_WIDTH, flexShrink: 0 }} gap={3}>
              <Box ref={setSectionRef(0)}>
                {learnerInfo && <LearnerInfoSection data={learnerInfo} />}
              </Box>
              <Box ref={setSectionRef(1)}>
                {learnerId && <CurrentProgressSection learnerId={learnerId} />}
              </Box>
            </Stack>
            <Box
              sx={{
                width: RIGHT_COL_WIDTH,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                height: leftColHeight,
                minHeight: 0,
              }}
            >
              {learnerId && <CommentsSection learnerId={learnerId} />}
            </Box>
          </Stack>

          {/* Section: 所属機関様への報告 */}
          {companyReport.visible && (
            <Box ref={setSectionRef(2)}>
              <InnerBox p={3} showDivider={false} borderColor={divider.high} bordered title="所属機関様への報告">
                <Stack mt={1} gap={1}>
                  <Typography variant="cap12" color={black[70]}>
                    報告内容
                  </Typography>
                  <Typography variant="body14Semi" color={text.primary} sx={{ whiteSpace: "pre-wrap" }}>
                    {notes?.companyReport ?? ""}
                  </Typography>
                </Stack>
              </InnerBox>
            </Box>
          )}

          {/* Section: メモ (RSO: 登録支援機関内メモ / Company: 社内メモ) */}
          {memo.visible && (
            <Box ref={setSectionRef(menuTitles.length - 1)}>
              <InnerBox p={3} showDivider={false} borderColor={divider.high} bordered title={memo.title}>
                <Stack mt={1} gap={1}>
                  {memo.subtitle && (
                    <Typography variant="cap12" color={black[70]}>
                      {memo.subtitle}
                    </Typography>
                  )}
                  <Typography variant="body14Semi" color={text.primary} sx={{ whiteSpace: "pre-wrap" }}>
                    {/* Backend reuses rsoMemo field for all memo types (RSO and Company) */}
                    {notes?.rsoMemo ?? ""}
                  </Typography>
                </Stack>
              </InnerBox>
            </Box>
          )}

          <FooterContainer>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                戻る
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleEdit}
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              >
                編集
              </Button>
            </Stack>
          </FooterContainer>
        </ContentArea>
      </PageWrap>
    </>
  );
};

const options = { shouldForwardProp: (propName: string) => !["active"].includes(propName) };

const TitleContainer = styled(Stack)`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: self-start;
  width: ${PAGE_WIDTH}px;
  margin: 0 auto;
  margin-bottom: 16px;
`;

const PageWrap = styled(Stack)`
  flex-direction: row;
  gap: 24px;
  display: flex;
  justify-content: space-between;
  width: ${PAGE_WIDTH}px;
  margin: 0 auto;
`;

const Menu = styled(Stack)`
  gap: 8px;
  min-width: 200px;
  position: sticky;
  top: ${STICKY_TOP}px;
  height: fit-content;
  align-self: flex-start;
`;

const MenuItem = styled(Box, options)<{ active: boolean }>`
  position: relative;
  border-left: 4px solid ${divider.middle};
  padding: 8px 16px;
  padding-left: 12px;
  cursor: pointer;
  max-width: 210px;
  ${css(typography.body14)};
  ${({ active }) =>
    active &&
    css`
      border-color: ${button.primary};
      pointer-events: none;
    `};
`;

const ContentArea = styled(Stack)`
  flex: 1;
  max-width: ${CONTENT_WIDTH}px;
  gap: 32px;
`;
